import React from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import { NSTable } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';

export interface ITotalPotentialIncomeTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const TotalPotentialIncomeTable = ({ operationBeginIndex, baseCashFlowColumns }: ITotalPotentialIncomeTableProps) => {
    const {
        incomeSummary, showCents, formatDisplayValue, operationsCashFlow,
    } = useProFormaSocketContext();

    const totalPotentialIncomeCashFlowColumns: ColumnDef<any>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `total-potential-income-${column.id}`,
        footer: () => (
            <RenderIf isTrue={operationBeginIndex}>
                <span className="text-dark">
                    {formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.totalPotentialIncome || 0, showCents, 'currency')}
                </span>
            </RenderIf>
        ),
    }));

    const totalPotentialIncomeColumns: ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            footer: () => <span>Total potential income</span>,
        },
        {
            accessorKey: 'annualGrowth',
            id: 'annualGrowth',
        },
        {
            accessorKey: 'percentOfTotalRent',
            id: 'percentOfTotalRent',
        },
        {
            accessorKey: 'pricePerSf',
            id: 'pricePerSf',
            footer: () => <span>{formatDisplayValue(incomeSummary.totalPotentialIncome.totalPricePerSf || 0, true, 'currency')}</span>,
        },
        {
            accessorKey: 'pricePerUnit',
            id: 'pricePerUnit',
            footer: () => <span>{formatDisplayValue(incomeSummary.totalPotentialIncome.totalPricePerUnit || 0, true, 'currency')}</span>,
        },
        {
            accessorKey: 'amountPerYear',
            id: 'amountPerYear',
            footer: () => <span>{formatDisplayValue(incomeSummary.totalPotentialIncome.totalAmountPerYear || 0, showCents, 'currency')}</span>,
        },
        ...totalPotentialIncomeCashFlowColumns,
    ];

    const table = useReactTable({
        data: [],
        columns: totalPotentialIncomeColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className="NSTable TotalPotentialIncomeTable mb-1">
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th py-1 px-2">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default TotalPotentialIncomeTable;
